import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
const entranceEase = [0.25, 0.46, 0.45, 0.94] as const;
export function PainSection() {
  const {
    lang
  } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const situations = [{
    tag: '01',
    text: 'Tienes la idea y el talento, pero no sabes por dónde empezar ni cómo presentarte profesionalmente.',
    textEn: "You have the idea and the talent, but you don't know where to start or how to present yourself professionally."
  }, {
    tag: '02',
    text: 'Ya tienes algo creado, pero se ve desorganizado y no transmite el verdadero valor de quién eres.',
    textEn: "You already have something created, but it looks disorganized and doesn't convey the true value of who you are."
  }, {
    tag: '03',
    text: 'Tienes presencia pero sin un sistema claro, sin un mensaje definido y sin saber cómo monetizar.',
    textEn: 'You have a presence but without a clear system, without a defined message, and without knowing how to monetize.'
  }];
  return <section id="problema" className="relative pt-16 pb-16 lg:pt-[100px] lg:pb-[100px] bg-[linear-gradient(135deg,#2E1F47_0%,#5A4579_100%)]" data-id="element-283">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-284">
        {/* ─────────────── HEADER ─────────────── */}
        <div className="text-center mb-12 lg:mb-20" data-id="element-285">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="flex flex-col items-center gap-4 mb-8" data-id="element-286">
            <span className="block w-[1px] h-[40px] bg-white/30" data-id="element-287" />
            <span className="inline-block font-sans text-[11px] font-semibold tracking-[3px] uppercase text-white" data-id="element-288">
              {lang === 'es' ? 'EL PUNTO DE PARTIDA' : 'THE STARTING POINT'}
            </span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: 'easeOut',
          delay: 0.1
        }} className="text-[32px] lg:text-[56px] font-serif text-white leading-[1.15] tracking-[-0.5px] max-w-[800px] mx-auto" data-id="element-289">
            {lang === 'es' ? <>
                ¿Te suena familiar{' '}
                <em className="italic text-[#A78BCA]" data-id="element-290">alguna</em> de estas?
              </> : <>
                Do any of these sound{' '}
                <em className="italic text-[#A78BCA]" data-id="element-291">familiar?</em>
              </>}
          </motion.h2>
        </div>

        {/* ─────────────── DESKTOP / TABLET CARDS ─────────────── */}
        <div className="hidden md:grid grid-cols-3 gap-6 mb-0 max-w-[1000px] mx-auto" data-id="element-292">
          {situations.map((sit, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: entranceEase,
          delay: index * 0.15
        }} className="group relative bg-[#FAF8F5] rounded-[16px] p-7 shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:-translate-y-2 hover:shadow-[0_16px_48px_rgba(0,0,0,0.3),inset_0_2px_0_0_#A78BCA] transition-all duration-[250ms] ease-in-out flex flex-col items-start overflow-hidden min-h-[300px]" data-id="element-293">
              {/* Ghost number watermark */}
              <span className="pointer-events-none select-none absolute -top-6 -right-2 font-serif font-semibold text-[140px] leading-none text-[rgba(90,69,121,0.08)] group-hover:text-[rgba(90,69,121,0.15)] transition-colors duration-300" data-id="element-294">
                {sit.tag}
              </span>

              <div className="relative z-10 mt-auto" data-id="element-295">
                <span className="block font-sans text-[10px] font-bold tracking-[3px] uppercase text-[#5A4579] mb-3" data-id="element-296">
                  SITUACIÓN {sit.tag}
                </span>
                <p className="text-[15px] text-[#1A1A2E] leading-[1.7] font-light" data-id="element-297">
                  {lang === 'es' ? sit.text : sit.textEn}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* ─────────────── MOBILE ACCORDION ─────────────── */}
        <div className="flex flex-col gap-[10px] md:hidden max-w-[1000px] mx-auto" data-id="element-298">
          {situations.map((sit, index) => {
          const isOpen = openIndex === index;
          const body = lang === 'es' ? sit.text : sit.textEn;
          const firstLine = body.split('. ')[0] + (body.includes('. ') ? '.' : '');
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            ease: entranceEase,
            delay: index * 0.15
          }} className="relative bg-[#FAF8F5] rounded-[16px] shadow-[0_8px_32px_rgba(0,0,0,0.2)] overflow-hidden" data-id="element-299">
                <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)} aria-expanded={isOpen} className="w-full text-left p-5 flex items-start gap-3" data-id="element-300">
                  <div className="flex-1 min-w-0" data-id="element-301">
                    <span className="block font-sans text-[10px] font-bold tracking-[3px] uppercase text-[#5A4579] mb-2" data-id="element-302">
                      SITUACIÓN {sit.tag}
                    </span>
                    {!isOpen && <p className="text-[15px] text-[#1A1A2E] leading-[1.7] font-light truncate" data-id="element-303">
                        {firstLine}
                      </p>}
                  </div>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="shrink-0 mt-0.5 text-[#5A4579]" data-id="element-304">
                    <PlusIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-305" />
                  </motion.span>
                </button>

                <motion.div initial={false} animate={{
              height: isOpen ? 'auto' : 0,
              opacity: isOpen ? 1 : 0
            }} transition={{
              duration: 0.3,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-306">
                  <p className="px-5 pb-5 text-[15px] text-[#1A1A2E] leading-[1.7] font-light" data-id="element-307">
                    {body}
                  </p>
                </motion.div>
              </motion.div>;
        })}
        </div>

        {/* ─────────────── CLOSING QUOTE ─────────────── */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        ease: 'easeOut',
        delay: 0.4
      }} className="text-center max-w-[820px] mx-auto mt-10 lg:mt-[60px]" data-id="element-308">
          <p className="text-[20px] lg:text-[24px] font-serif italic text-white leading-[1.35]" data-id="element-309">
            {lang === 'es' ? '"Si te identificaste con alguna — estás en el lugar correcto."' : '"If you identified with any of these — you are in the right place."'}
          </p>
        </motion.div>
      </div>
    </section>;
}